import {
  Control,
  Controls,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { Department } from '@trustero/trustero-api-web/lib/model/department_pb'
import {
  PolicyRecord,
  PolicyRecords,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ComplianceFrameworks } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  Framework,
  Frameworks,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import FileSaver from 'file-saver'
import * as CSV from 'csv-string'
import {
  AuditReadinessRecord,
  AuditReadinessTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import {
  getAdvancedText,
  getControlTestName,
  getDateText,
  getReasonText,
  getSingleCheckResultText,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { ResponsibilityOptions } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  OeControlTestCitation,
  OeControlTestCitationType,
  OeControlTestDetermination,
  OeControlTestResult,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import dayjs from 'dayjs'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { controlStatusTitle } from '../../lib/common/types'
import { NONE_NAME, NO_REQUIREMENTS_INDICATOR } from '../globalConstants'
import { getFrameworkGroups } from '../globalHelpers'
import { ComplianceFrameworkGroup } from '../globalTypes'

const CONTROLS_CSV_HEADERS = [
  'Control ID',
  'Control Name',
  'Objective',
  'Notes',
  'Procedure',
  'Policy',
  'Department',
  'Status',
  'Responsibility',
  'Reason',
  'Vendor',
  'Owner',
  'Assignees',
  'Required Evidence',
  'Test Procedures',
  'Risks',
]

// Appended after CONTROLS_CSV_HEADERS only when the caller supplies a page context to build links
// from; a link column of empty cells is worse than none. Safe to add to a re-importable export:
// the importer declares it optional and prepareControlsUpload never reads it, the same way the
// framework and control-test columns ride along.
const URL_HEADER = 'URL'

const nameResult = (name: string) => `${getControlTestName(name)} - Result`
const nameReason = (name: string) => `${getControlTestName(name)} - Reason`
const nameDate = (name: string) =>
  `${getControlTestName(name)} - Date Performed`
const nameAdvanced = (name: string) =>
  `${getControlTestName(name)} - Using Advanced Control Test`
const nameDetails = (name: string) => `${getControlTestName(name)} - Details`

const quoteRequirementForList = (value: string): string => {
  const escaped = value.replace(/"/g, '""')
  return `"${escaped}"`
}

// Excel/Sheets treat an UNQUOTED cell whose first character is one of = + - @
// (or a leading tab/CR) as a formula, turning text like evidence starting with
// such a character into a `#NAME?` error. Prefix those with a single quote so
// they're read as text (Excel hides the apostrophe on unquoted fields).
//
// We only do this for cells that will be emitted unquoted. csv-string quotes any
// field containing a comma, quote, or newline (e.g. the multi-line markdown test
// procedures), and Excel both reads those as text already AND shows a leading
// apostrophe literally inside the quotes — so escaping them would be both
// unnecessary and visibly wrong.
//
// A leading "- "/"+ " is a markdown bullet (e.g. a single-item required-evidence
// list), not a spreadsheet formula, so it's exempted — otherwise a one-bullet
// cell gets an apostrophe that both shows literally outside Excel and corrupts
// the value on re-import. Multi-bullet lists already carry a newline and so are
// quoted (never prefixed); this aligns the single-bullet case with them.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/
const MARKDOWN_BULLET = /^[-+]\s/
const NEEDS_CSV_QUOTING = /[",\n\r]/
const sanitizeCsvCell = (value: string): string =>
  FORMULA_TRIGGER.test(value) &&
  !MARKDOWN_BULLET.test(value) &&
  !NEEDS_CSV_QUOTING.test(value)
    ? `'${value}`
    : value

// Test procedures from the new model's repeated test_procedures on the Control,
// formatted to match the backend's ReconstructTestProceduresText.
const formatTestProcedures = (control: Control): string =>
  (control.getSuggestions()?.getTestProceduresList() ?? [])
    .map((tp) => {
      const title = tp.getTitle().trim()
      const body = tp.getBody().trim()
      return title ? `- **${title}:** ${body}` : body
    })
    .filter((line) => line.length > 0)
    .join('\n')

// Suggested/required evidence from the new model (batch-loaded onto the Control).
const formatSuggestedEvidence = (control: Control): string =>
  control.getSuggestions()?.getSuggestedEvidence()?.getBody()?.trim() ?? ''

const showPageLink = (
  pageContext: string,
  modelType: MODEL_TYPE,
  modelId: string,
): string =>
  generatePermalink({
    pageContext,
    modelType,
    modelId,
    // Absolute: the csv is read outside the app, where a relative path means nothing.
    isInternalLink: false,
  })

// One cited source: its caption, when it was relevant, a link to it, and what the check made of it.
//
// Evidence absent from evidenceLinkIds has been deleted since the test ran and gets no link — the
// server omits exactly those, matching the audit-scan UI, which renders them "(Deleted)".
const formatCitation = (
  citation: OeControlTestCitation.AsObject,
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string => {
  const isContext = citation.citationType === OeControlTestCitationType.CONTEXT
  const title = isContext ? 'Organizational Context' : citation.evidenceCaption
  const formattedDate = citation.relevantDate
    ? dayjs(citation.relevantDate).format(DATE_FORMATS.ISO_WITH_TIME)
    : ''

  const lines = [formattedDate ? `${title}  ${formattedDate}` : title]
  const linkId = evidenceLinkIds.get(citation.evidenceId)
  if (linkId && pageContext) {
    lines.push(showPageLink(pageContext, MODEL_TYPE.EVIDENCE, linkId))
  }
  if (citation.findingSummary) {
    lines.push(citation.findingSummary)
  }
  return lines.join('\n')
}

// The latest Operating Effectiveness test, one block per test procedure, mirroring the "copy
// summary and details" text the audit-scan UI produces so the csv reads the same way.
const formatTestDetails = (
  details: OeControlTestResult.AsObject[],
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string =>
  details
    .map((result, index) => {
      const icon =
        result.determination === OeControlTestDetermination.PASS ? '✓' : '✗'
      let text = `${icon} ${index + 1}. ${result.testString}\n${
        result.reasoning
      }`
      if (result.citationsList.length) {
        const citationText = result.citationsList
          .map((citation) =>
            formatCitation(citation, pageContext, evidenceLinkIds),
          )
          .join('\n\n')
        text += `\n\nEvidence and Sources:\n${citationText}`
      }
      return text
    })
    .join('\n\n')

const generateControlsReport = async (
  controls: Control[],
  policies: Record<string, PolicyRecord>,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Record<string, Department>,
  vendors: Record<string, VendorRecord>,
  frameworkGroups: Record<string, ComplianceFrameworkGroup>,
  risksMap: Record<string, Risk[]>,
  customStatusNamesById: Record<string, string>,
  controlTests?: Record<string, AuditReadinessTestRecord.AsObject[]>,
  pageContext = '',
  evidenceLinkIds: Map<string, string> = new Map(),
): Promise<Blob> => {
  const sortedCfNames = Object.keys(frameworkGroups).sort(
    (a: string, b: string) => a.localeCompare(b),
  )
  const testHeaders: Set<string> = controlTests
    ? Object.values(controlTests).reduce((set, list) => {
        list.forEach((test) => {
          set.add(nameResult(test.testName))
          set.add(nameReason(test.testName))
          set.add(nameDate(test.testName))
          set.add(nameAdvanced(test.testName))
          set.add(nameDetails(test.testName))
        })
        return set
      }, new Set<string>())
    : new Set<string>()

  const updatedHeaders = [
    ...CONTROLS_CSV_HEADERS,
    ...(pageContext ? [URL_HEADER] : []),
    ...sortedCfNames,
    ...Array.from(testHeaders),
  ]

  const data = controls.map((control: Control) => {
    const department = departments[control.getDepartmentId()]
    const frameworkIds = new Set(control.getFrameworkIdsList())
    const controlId = control.getMigratedControlId() || control.getId()
    const newControlId = control.getId().trim()
    const oldControlId = control.getMigratedControlId().trim()
    const policyNames = controlPolicyNamesByControlId[control.getId()] || []
    const policyNameForCsv =
      policyNames.length > 0
        ? policyNames.join('|')
        : policies[control.getPolicyModelId()]?.getName() || NONE_NAME
    const departmentNames =
      controlDepartmentNamesByControlId[control.getId()] || []
    const departmentNameForCsv =
      departmentNames.length > 0
        ? departmentNames.join('|')
        : department?.getName() || NONE_NAME

    const row = [
      control.getModelId(),
      control.getName().trim(),
      control.getObjective().replace('##', '').trim(),
      control.getNote().trim(),
      (control.getProcedure() ?? '').trim(),
      policyNameForCsv,
      departmentNameForCsv,
      customStatusNamesById[control.getCustomStatusId()] ||
        controlStatusTitle[control.getStatus()],
      ResponsibilityOptions[control.getResponsibility()],
      control.getNotApplicableReason(),
      vendors[control.getVendorId()]?.getName() || '',
      control.getOwnerEmail(),
      control.getAssigneeEmailsList().join(','),
      formatSuggestedEvidence(control),
      formatTestProcedures(control),
      risksMap[newControlId]
        ?.map((risk) => `${risk.getCustomId()} ${risk.getName()}`)
        .join(',') ||
        risksMap[oldControlId]
          ?.map((risk) => `${risk.getCustomId()} ${risk.getName()}`)
          .join(',') ||
        '',
    ]

    if (pageContext) {
      row.push(
        showPageLink(pageContext, MODEL_TYPE.CONTROL, control.getModelId()),
      )
    }

    const linkedCfbIds = new Set(control.getComplianceFrameworkIdsList())
    sortedCfNames.forEach((cfName: string) => {
      const group = frameworkGroups[cfName]
      const frameworks = group.objectives
        .filter((objective: Framework) =>
          frameworkIds.has(objective.getModelId()),
        )
        .map((objective: Framework) =>
          quoteRequirementForList(objective.getName()),
        )
        .join(',')
      if (frameworks) {
        row.push(frameworks)
      } else if (
        linkedCfbIds.has(group.base.getId()) ||
        linkedCfbIds.has(group.base.getModelId())
      ) {
        // Linked to the framework but no requirement links — emit the indicator
        // so the import re-creates the framework link with no requirements.
        row.push(NO_REQUIREMENTS_INDICATOR)
      } else {
        row.push('')
      }
    })
    const tests =
      controlTests && controlId in controlTests ? controlTests[controlId] : []

    // add columns for each test header
    Array.from(testHeaders).forEach((_) => row.push(''))

    tests.forEach((check) => {
      const resultIdx = updatedHeaders.findIndex(
        (elem) => elem === nameResult(check.testName),
      )
      const reasonIdx = updatedHeaders.findIndex(
        (elem) => elem === nameReason(check.testName),
      )
      const dateIdx = updatedHeaders.findIndex(
        (elem) => elem === nameDate(check.testName),
      )
      const advancedIdx = updatedHeaders.findIndex(
        (elem) => elem === nameAdvanced(check.testName),
      )
      const detailsIdx = updatedHeaders.findIndex(
        (elem) => elem === nameDetails(check.testName),
      )

      row[resultIdx] = getSingleCheckResultText(
        check.result,
        check.testName as CONTROL_TEST_NAMES,
      )
      row[reasonIdx] = getReasonText(check.reason)
      row[dateIdx] = getDateText(check.updatedAt)
      row[advancedIdx] = getAdvancedText(check.artifactId)
      row[detailsIdx] = formatTestDetails(
        check.detailsList,
        pageContext,
        evidenceLinkIds,
      )
    })

    return row
  })
  const sanitizedData = data.map((row) =>
    row.map((cell) => sanitizeCsvCell(String(cell ?? ''))),
  )
  const stringHeader = [updatedHeaders.join(',')]
  const csvRows = stringHeader.concat(CSV.stringify(sanitizedData)).join('\r\n')
  return new Blob([csvRows], { type: 'text/csv;charset=utf-8;' })
}

export const DownloadControlsReport = async (
  controls: Controls | undefined,
  policies: PolicyRecords | undefined,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Department[] | undefined,
  vendors: VendorRecord[] | undefined,
  complianceFrameworks: ComplianceFrameworks | undefined,
  cfObjectives: Frameworks | undefined,
  risks: Risk[] | undefined,
  controlStatuses: ControlStatusItem[] | undefined,
  controlCheckData?: AuditReadinessRecord[],
  // The account (and audit) the links belong to, from the route. Omitted by callers that build
  // links for nothing — without it the URL column and the citation links are left out.
  pageContext?: string,
  evidenceLinkIds?: Map<string, string>,
): Promise<void> => {
  const ctrls = controls?.getItemsList() || []
  const policiesMap = (policies?.getItemsList() || []).reduce(
    (acc: Record<string, PolicyRecord>, policy: PolicyRecord) => {
      acc[policy.getModelId()] = policy
      return acc
    },
    {},
  )
  const deptMap = (departments || []).reduce(
    (acc: Record<string, Department>, dept: Department) => {
      acc[dept.getId()] = dept
      acc[dept.getModelId()] = dept
      return acc
    },
    {},
  )

  const frameworkGroups = getFrameworkGroups(complianceFrameworks, cfObjectives)

  const controlTestsMap = (controlCheckData || []).reduce(
    (
      acc: Record<string, AuditReadinessTestRecord.AsObject[]>,
      record: AuditReadinessRecord,
    ) => {
      acc[record.getControlId()] = record
        .getControlTestsList()
        .map((ele) => ele.toObject())

      return acc
    },
    {},
  )

  const vendorsMap = (vendors || []).reduce(
    (acc: Record<string, VendorRecord>, vendor: VendorRecord) => {
      acc[vendor.getId()] = vendor
      return acc
    },
    {},
  )

  const risksMap = (risks || []).reduce(
    (acc: Record<string, Risk[]>, risk: Risk) => {
      risk
        .getControlIds()
        .split(',')
        .forEach((controlId) => {
          const trimmedControlId = controlId.trim()
          if (!acc[trimmedControlId]) {
            acc[trimmedControlId] = []
          }
          acc[trimmedControlId].push(risk)
        })
      return acc
    },
    {},
  )

  const customStatusNamesById = (controlStatuses || []).reduce(
    (acc: Record<string, string>, status: ControlStatusItem) => {
      acc[status.getId()] = status.getName()
      return acc
    },
    {},
  )

  const csv = await generateControlsReport(
    ctrls,
    policiesMap,
    controlPolicyNamesByControlId,
    controlDepartmentNamesByControlId,
    deptMap,
    vendorsMap,
    frameworkGroups,
    risksMap,
    customStatusNamesById,
    controlTestsMap,
    pageContext,
    evidenceLinkIds,
  )
  await FileSaver.saveAs(
    csv,
    controlCheckData ? 'controls-with-checks.csv' : 'control-detail.csv',
  )
}
